import { FulcrumLimits, REACTIVE_POWER_FUNCTION_MODEL, ValueWithLimits } from '../../types/common'
import { ReactivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { ReactivePowerSettingsForm } from '../../types/schemas'
import { isFieldDisabled, phaseShiftDependingOnValue } from '../../utils'

export const getDefaultValues = (data: ReactivePowerSettingsModel): ReactivePowerSettingsForm => {
  return {
    selectedFunction: data.selectedFunction || REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi,
    threeTau: data.threeTau.value,
    minCosPhi: data.minCosPhi?.value || null,
    fulcrums: mapFulcrumsToFormValues(data.fulcrums),
  }
}

const mapFulcrumsToFormValues = (fulcrums: ReactivePowerSettingsModel['fulcrums']): ReactivePowerSettingsForm['fulcrums'] => {
  return {
    [REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]: [
      (() => {
        const found = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi in f)?.[REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi].value
        const value = found?.value

        if (value === undefined || value === null) return [null, 0, 0]

        return [null, value, phaseShiftDependingOnValue(value)]
      })(),
    ],
    [REACTIVE_POWER_FUNCTION_MODEL.cosPhi]:
      fulcrums
        .find(f => REACTIVE_POWER_FUNCTION_MODEL.cosPhi in f)
        ?.[REACTIVE_POWER_FUNCTION_MODEL.cosPhi].values.map(([a, b]) => [a, b, phaseShiftDependingOnValue(b)]) || [],
    [REACTIVE_POWER_FUNCTION_MODEL.qU]:
      fulcrums
        .find(f => REACTIVE_POWER_FUNCTION_MODEL.qU in f)
        ?.[REACTIVE_POWER_FUNCTION_MODEL.qU].values.map(([a, b]) => [a, b, phaseShiftDependingOnValue(b)]) || [],
    [REACTIVE_POWER_FUNCTION_MODEL.fixedQ]: [
      (() => {
        const found = fulcrums.find(f => REACTIVE_POWER_FUNCTION_MODEL.fixedQ in f)?.[REACTIVE_POWER_FUNCTION_MODEL.fixedQ].value
        const value = found?.value

        if (value === undefined || value === null) return [null, 0, 0]

        return [null, value, phaseShiftDependingOnValue(value)]
      })(),
    ],
  }
}

export const getFirstFieldDisabledState = (
  isFixedFunction: boolean,
  limits: FulcrumLimits[],
  fulcrumValue: number | null,
  index: number
): boolean => {
  // ? fixed functions have no first field
  if (isFixedFunction) return true
  return isFieldDisabled({ value: fulcrumValue, min: limits[index].x.min, max: limits[index].x.max })
}

export const getSecondFieldDisabledState = (limits: FulcrumLimits[] | ValueWithLimits, fulcrumValue: number | null, index: number): boolean => {
  if (Array.isArray(limits)) return isFieldDisabled({ value: fulcrumValue, min: limits[index].y.min, max: limits[index].y.max })
  else return isFieldDisabled({ value: fulcrumValue, min: limits.min, max: limits.max })
}

interface AvailableFunctionOption {
  value: REACTIVE_POWER_FUNCTION_MODEL
  label: string
}

export const getAvailableFunctionOptions = (fulcrums: ReactivePowerSettingsModel['fulcrums']): AvailableFunctionOption[] => {
  return Object.values(REACTIVE_POWER_FUNCTION_MODEL)
    .filter(value => Object.values(fulcrums).some(v => Object.keys(v)[0] === value))
    .map(value => ({
      value,
      label: `[${value}]`,
    }))
}
export const getFulcrumFieldHasChangedValues = (
  selectedFunction: REACTIVE_POWER_FUNCTION_MODEL,
  fulcrumItem: ReactivePowerSettingsModel['fulcrums'][number],
  index: number
): { firstFieldHasChangedValue: boolean; secondFieldHasChangedValue: boolean } => {
  let firstFieldHasChangedValue = false
  let secondFieldHasChangedValue = false

  if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi && REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi in fulcrumItem) {
    secondFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi].value.changed
  } else if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.cosPhi && REACTIVE_POWER_FUNCTION_MODEL.cosPhi in fulcrumItem) {
    firstFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.cosPhi].limits[index].x.changed
    secondFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.cosPhi].limits[index].y.changed
  } else if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.qU && REACTIVE_POWER_FUNCTION_MODEL.qU in fulcrumItem) {
    firstFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.qU].limits[index].x.changed
    secondFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.qU].limits[index].y.changed
  } else if (selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixedQ && REACTIVE_POWER_FUNCTION_MODEL.fixedQ in fulcrumItem) {
    secondFieldHasChangedValue = fulcrumItem[REACTIVE_POWER_FUNCTION_MODEL.fixedQ].value.changed
  }

  return { firstFieldHasChangedValue, secondFieldHasChangedValue }
}
